"use client";

import { useState, useRef, useEffect } from "react";
import { MessageCircle, Phone, X, MessageSquare, Send } from "lucide-react";
import Image from "next/image";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";

export function WhatsAppButton() {
    const [isOpen, setIsOpen] = useState(false);
    const [isPulsing, setIsPulsing] = useState(true);
    const containerRef = useRef<HTMLDivElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    // Stop pulsing after 5 seconds
    useEffect(() => {
        const timer = setTimeout(() => {
            setIsPulsing(false);
        }, 5000);
        return () => clearTimeout(timer);
    }, []);

    useGSAP(() => {
        if (isOpen) {
            gsap.to(menuRef.current, {
                autoAlpha: 1,
                y: 0,
                scale: 1,
                duration: 0.4,
                ease: "back.out(1.2)"
            });
        } else {
            gsap.to(menuRef.current, {
                autoAlpha: 0,
                y: 20,
                scale: 0.9,
                duration: 0.3,
                ease: "power2.in"
            });
        }
    }, { scope: containerRef, dependencies: [isOpen] });

    return (
        <div ref={containerRef} className="fixed bottom-6 right-6 z-50 flex flex-col items-end pointer-events-none">

            {/* Modern Support Card */}
            <div
                ref={menuRef}
                className="invisible opacity-0 translate-y-4 scale-90 origin-bottom-right mb-4 w-[300px] bg-white/80 backdrop-blur-xl border border-white/40 shadow-2xl rounded-3xl overflow-hidden pointer-events-auto"
            >
                {/* Header with Avatar */}
                <div className="bg-gradient-to-r from-blue-900 to-blue-800 p-6 text-white relative overflow-hidden">
                    {/* Decorative Circles */}
                    <div className="absolute top-[-20px] right-[-20px] w-20 h-20 bg-white/10 rounded-full blur-xl"></div>
                    <div className="absolute bottom-[-10px] left-[-10px] w-16 h-16 bg-blue-500/20 rounded-full blur-lg"></div>

                    <div className="flex items-center gap-4 relative z-10">
                        <div className="relative">
                            <div className="w-12 h-12 rounded-full border-2 border-white/50 overflow-hidden bg-white/10 p-0.5">
                                <Image
                                    src="/images/technician.webp"
                                    alt="Support Agent"
                                    width={48}
                                    height={48}
                                    className="rounded-full object-cover w-full h-full"
                                />
                            </div>
                            <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-blue-900 rounded-full"></span>
                        </div>
                        <div>
                            <h3 className="font-bold text-lg leading-tight">Support Team</h3>
                            <p className="text-blue-200 text-xs">Always here to help 👋</p>
                        </div>
                    </div>
                </div>

                {/* Body */}
                <div className="p-5 bg-white/50">
                    <p className="text-gray-600 text-sm mb-5 leading-relaxed">
                        Have a question? Chat with us directly or give us a call for urgent support.
                    </p>

                    <div className="space-y-3">
                        <a
                            href="https://wa.me/94707336060"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-center gap-3 w-full py-3.5 bg-[#25D366] hover:bg-[#20bd5a] text-white rounded-xl font-bold shadow-lg shadow-green-500/20 transition-all active:scale-95 group"
                        >
                            <MessageCircle className="w-5 h-5" />
                            <span>Start WhatsApp Chat</span>
                        </a>

                        <a
                            href="tel:+94114502905"
                            className="flex items-center justify-center gap-3 w-full py-3.5 bg-white hover:bg-gray-50 text-gray-800 border-2 border-gray-100 rounded-xl font-bold transition-all active:scale-95 group"
                        >
                            <Phone className="w-4 h-4 text-gray-500 group-hover:text-blue-600 transition-colors" />
                            <span>Call Support</span>
                        </a>
                    </div>
                </div>
            </div>

            {/* Toggle Button (Glassmorphic) */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="relative group focus:outline-none pointer-events-auto"
                aria-label="Toggle support menu"
            >
                {/* Ripple ring */}
                {isPulsing && !isOpen && (
                    <span className="absolute inset-0 -m-1 rounded-full border-2 border-blue-500/30 opacity-75 animate-ping"></span>
                )}

                <div className={`
                    relative w-16 h-16 rounded-full flex items-center justify-center shadow-[0_8px_30px_rgb(0,0,0,0.12)] backdrop-blur-sm transition-all duration-300
                    ${isOpen
                        ? 'bg-gray-900 rotate-90 scale-90'
                        : 'bg-gradient-to-br from-blue-600 to-blue-700 hover:scale-105 rotate-0'
                    }
                `}>
                    {isOpen ? (
                        <X className="w-7 h-7 text-white" />
                    ) : (
                        <div className="relative">
                            <MessageSquare className="w-7 h-7 text-white fill-white/20" />
                            <span className="absolute -top-1 -right-1 w-3 h-3 bg-green-400 border-2 border-blue-600 rounded-full"></span>
                        </div>
                    )}
                </div>
            </button>
        </div>
    );
}
